/**
 * React hooks over the shared SkyDent damage-status store.
 *
 * The Damage Location is owned by SkyDent (not stored on the Aether event), so
 * it is read per damage event through the SkyDent proxy and cached in a shared
 * store. These hooks subscribe to that store via useSyncExternalStore so a save
 * in the SkyDent modal (which refreshes the store) reliably updates the
 * homepage/History flag, the IROP affordance, and the save gate together.
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { UnscheduledEvent } from '../types';
import {
  SkyDentDamageStatus,
  ensureSkyDentDamageStatus,
  getSkyDentDamageStatus,
  getSkyDentStoreVersion,
  subscribeSkyDentDamageStatus,
} from '../services/damageLocationStatus';

/**
 * Resolves which damage-flagged events still have an incomplete SkyDent Damage
 * Location, for flagging them in the homepage/History IROP column.
 *
 * @param events The events currently displayed in the table.
 * @param enabled When false, no lookups run and an empty set is returned.
 * @returns The set of eventIds whose Damage Location is not yet completed.
 */
export function useDamageLocationStatus(events: UnscheduledEvent[], enabled: boolean): Set<string> {
  const version = useSyncExternalStore(subscribeSkyDentDamageStatus, getSkyDentStoreVersion);

  useEffect(() => {
    if (!enabled) return;
    for (const event of events) {
      if (event.damage && event.eventId) ensureSkyDentDamageStatus(event.eventId);
    }
  }, [events, enabled]);

  return useMemo<Set<string>>(() => {
    const missing = new Set<string>();
    if (!enabled) return missing;
    for (const event of events) {
      if (event.damage && event.eventId && getSkyDentDamageStatus(event.eventId)?.locationMissing) {
        missing.add(event.eventId);
      }
    }
    // `version` participates so the set is recomputed after async resolution.
    return missing;
  }, [events, enabled, version]);
}

/**
 * Resolves the SkyDent damage status for a single event (record existence and
 * whether its Damage Location is blank).
 *
 * @param eventId The Aether event id (undefined for unsaved records).
 * @param enabled When false, no lookup runs and undefined is returned.
 * @returns The status, or undefined until it resolves.
 */
export function useSkyDentDamageStatus(
  eventId: string | undefined,
  enabled: boolean,
): SkyDentDamageStatus | undefined {
  const version = useSyncExternalStore(subscribeSkyDentDamageStatus, getSkyDentStoreVersion);

  useEffect(() => {
    if (!enabled || !eventId) return;
    ensureSkyDentDamageStatus(eventId);
  }, [eventId, enabled]);

  return useMemo<SkyDentDamageStatus | undefined>(() => {
    if (!enabled || !eventId) return undefined;
    // `version` participates so consumers re-read after async resolution.
    return getSkyDentDamageStatus(eventId);
  }, [eventId, enabled, version]);
}
